// Pure logic for the Finance Visualization Mode (#442) — no React, no DOM.
//
// The ARITHMETIC (currency conversion, per-period and per-category totals,
// percentages) is done server-side by GET /api/expenses/analytics, through the
// same converter the ledger's Net Balance uses. What lives here is everything
// the FE decides on its own: which dates a range preset means, which grouping a
// range defaults to, how a long tail of categories folds into "Other", which
// colour a category wears, and the geometry of the pie. Bar geometry reuses
// `dailyEnergyChart.js` (the Energy HUB's bar chart) rather than a second copy.

// ── Dates ────────────────────────────────────────────────────────────────────
// Ranges are the VIEWER'S calendar days (the user means "the last 10 days" where
// they are), formatted as plain YYYY-MM-DD and sent as-is; the backend filters
// on the transaction's stored date, which is also a plain calendar date.

const pad = (n) => String(n).padStart(2, '0');

/** A Date's LOCAL calendar day as YYYY-MM-DD. */
export const localIsoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const parseIso = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
};

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export const RANGE_PRESETS = ['10d', '15d', '30d', 'month', '12m'];

/**
 * The inclusive {start, end} a preset means, ending on `today` (a Date).
 * "Last 10 days" is ten calendar days INCLUDING today. "This month" runs from
 * the 1st to today. "Last 12 months" starts on the 1st of the month eleven
 * months back, so it is exactly twelve whole-month bars.
 */
export function rangeForPreset(preset, today = new Date()) {
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const back = { '10d': 9, '15d': 14, '30d': 29 }[preset];
    if (back !== undefined) return { start: localIsoDate(addDays(end, -back)), end: localIsoDate(end) };
    if (preset === 'month') return { start: localIsoDate(new Date(end.getFullYear(), end.getMonth(), 1)), end: localIsoDate(end) };
    if (preset === '12m') return { start: localIsoDate(new Date(end.getFullYear(), end.getMonth() - 11, 1)), end: localIsoDate(end) };
    return null;
}

/** Inclusive day count of a range; 0 when invalid or reversed. */
export function rangeDayCount(start, end) {
    const a = parseIso(start);
    const b = parseIso(end);
    if (!a || !b || b < a) return 0;
    // Round, not floor: a DST change makes one "day" 23 or 25 hours long.
    return Math.round((b - a) / 86_400_000) + 1;
}

export const isValidRange = (start, end) => rangeDayCount(start, end) > 0;

/** The backend's ceiling on daily bars per request. */
export const MAX_DAY_BARS = 366;

export const dayGroupingAllowed = (start, end) => {
    const n = rangeDayCount(start, end);
    return n > 0 && n <= MAX_DAY_BARS;
};

/** Ranges longer than ~two months read better as monthly bars. */
export const defaultGranularity = (start, end) => (rangeDayCount(start, end) > 62 ? 'month' : 'day');

// ── Categories ───────────────────────────────────────────────────────────────

/**
 * Categorical slots, validated as a set (dataviz validator, dark mode, on the
 * panel's #10151b glass): lightness band, chroma floor, adjacent CVD ΔE ≥ 8.4,
 * normal-vision ΔE ≥ 19, ≥ 3:1 against the surface. A ninth category is never a
 * generated hue — it folds into "Other".
 */
export const CATEGORY_PALETTE = [
    '#3987e5', // blue
    '#d95926', // orange
    '#199e70', // aqua
    '#c98500', // yellow
    '#d55181', // magenta
    '#008300', // green
    '#9085e9', // violet
    '#e66767', // red
];
/** "Other" is deliberately achromatic so it never reads as one more category. */
export const OTHER_COLOR = '#6b7280';
export const OTHER_KEY = '__other__';
export const MAX_SLICES = CATEGORY_PALETTE.length;

const hashString = (s) => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
};

/**
 * A colour per category that follows the CATEGORY, not its rank: "Meals" keeps
 * its colour when the range changes and Taxis overtakes it. Each name hashes to a
 * preferred slot; collisions probe to the next free slot, resolved in name order
 * so the outcome does not depend on the order the API listed them. Distinct
 * colours are guaranteed for up to MAX_SLICES categories (the most a pie shows).
 */
export function categoryColors(names) {
    const out = {};
    const taken = new Set();
    const unique = Array.from(new Set((names || []).filter((n) => n && n !== OTHER_KEY)));
    unique.sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()) || a.localeCompare(b));
    for (const name of unique) {
        const n = CATEGORY_PALETTE.length;
        let slot = hashString(name.toLocaleLowerCase()) % n;
        for (let k = 0; k < n && taken.has(slot); k += 1) slot = (slot + 1) % n;
        taken.add(slot);
        out[name] = CATEGORY_PALETTE[slot];
    }
    out[OTHER_KEY] = OTHER_COLOR;
    return out;
}

/**
 * At most MAX_SLICES slices: past that, the top (MAX_SLICES - 1) stay and the
 * rest fold into one "Other" slice whose amount, count and percentage are the
 * sums of what it absorbed (so the legend still adds up to 100%). `valueKey`
 * names the money field (#482: the pie reads the direction-neutral `amount`).
 */
export function foldCategories(list, max = MAX_SLICES, valueKey = 'expense') {
    const rows = (list || []).filter((c) => Number(c?.[valueKey]) > 0);
    if (rows.length <= max) return rows;
    const keep = rows.slice(0, max - 1);
    const rest = rows.slice(max - 1);
    const sum = (key) => rest.reduce((acc, r) => acc + Number(r[key] || 0), 0);
    return [
        ...keep,
        {
            category: OTHER_KEY,
            [valueKey]: Math.round(sum(valueKey) * 100) / 100,
            count: sum('count'),
            pct: Math.round(sum('pct') * 100) / 100,
            folded: rest.length,
            // #483: the labels "Other" absorbed — what its detail list asks for.
            members: rest.map((r) => r.category),
        },
    ];
}

/**
 * The category label(s) a slice's detail list requests (#483): the slice's own
 * label, or — for the folded "Other" slice — every label it absorbed. The
 * server matches them with the pie's own case-insensitive grouping.
 */
export function detailCategoriesFor(slice) {
    if (!slice) return [];
    if (slice.category === OTHER_KEY) return Array.isArray(slice.members) ? slice.members.slice() : [];
    return [slice.category];
}

/**
 * The pie's category list for the active Expenses/Income toggle (#482):
 * `by_category` (spending) or `income_by_category` (income) from
 * GET /api/expenses/analytics, each row normalised to carry its money under
 * `amount` (the server sends it; older payloads only had the direction key).
 * Never mixes the two directions; a missing list is an empty pie.
 */
export function pieCategoriesFor(data, direction) {
    const isIncome = direction === 'income';
    const list = (isIncome ? data?.income_by_category : data?.by_category) || [];
    const key = isIncome ? 'income' : 'expense';
    return list.map((c) => ({ ...c, amount: c?.amount ?? c?.[key] ?? 0 }));
}

/**
 * Start/end angles (degrees, clockwise from 12 o'clock) for each slice. Angles
 * come from the VALUES, not the rounded percentages, so the ring always closes
 * at exactly 360°.
 */
export function pieAngles(values) {
    const nums = (values || []).map((v) => Math.max(0, Number(v) || 0));
    const total = nums.reduce((a, b) => a + b, 0);
    if (!(total > 0)) return nums.map(() => null);
    let angle = 0;
    return nums.map((v) => {
        if (!(v > 0)) return null;
        const sweep = (v / total) * 360;
        const out = { start: angle, end: angle + sweep, sweep };
        angle += sweep;
        return out;
    });
}

/** Month bucket key 'YYYY-MM' → a Date on the 1st (local). */
export const periodToDate = (period) => {
    const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(period || ''));
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3] || 1));
};
